import { useState, useEffect, useCallback, useMemo, createContext, createElement, useContext, PropsWithChildren } from 'react';
import { useSessionContext, useSupabaseClient, useUser } from '../contexts/SessionContext';
import { User, Company } from '../types';
import { toast } from 'sonner';

// Estado de autenticação compartilhado por toda a aplicação (via <AuthProvider>).
// Antes cada componente que chamava useAuth() tinha sua própria cópia: carregava o
// perfil de novo e criava seu próprio timer de "ainda está ativo?" — com ~30 componentes
// na tela eram dezenas de consultas a `users` a cada 2 minutos por aba.
const ACTIVE_COMPANY_KEY = 'orion_active_company_id';

const useAuthState = () => {
  const { session, isLoading: isSessionLoading, error: sessionError } = useSessionContext();
  const supabase = useSupabaseClient();
  const userSupabase = useUser();

  const [profile, setProfile] = useState<User | null>(null);
  const [company, setCompany] = useState<Company | null>(null);
  // Permissões efetivas do usuário logado (role_permissions + override de
  // company_role_permissions, resolvidas no banco por my_permissions()).
  // É a fonte única de "quem pode o quê": o front só pergunta, nunca decide.
  const [permissions, setPermissions] = useState<string[]>([]);
  const [isGlobalSuperAdmin, setIsGlobalSuperAdmin] = useState(false);
  const [isParentSuperAdmin, setIsParentSuperAdmin] = useState(false);
  // Vazio para quem só tem a própria empresa; com 2+ o Topbar mostra o seletor.
  const [accessibleCompanies, setAccessibleCompanies] = useState<Company[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [profileFetchAttempted, setProfileFetchAttempted] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const fetchProfile = useCallback(async () => {
    if (userSupabase) {
      const { data, error } = await supabase
        .from('users')
        .select(`*, companies!users_company_id_fkey(*)`)
        .eq('id', userSupabase.id)
        .single();

      if (error) {
        setError('Não foi possível carregar o perfil do utilizador.');
        toast.error('Erro ao carregar perfil: ' + error.message);
        console.error(error);
      }
      if (data) {
        if (data.is_active === false) {
          toast.error('Seu acesso foi bloqueado. Fale com o administrador da sua empresa.');
          await supabase.auth.signOut();
          localStorage.clear();
          sessionStorage.clear();
          window.location.href = '/login';
          return;
        }

        // Empresas extras liberadas para este usuário (user_company_access), sem
        // mudar o cargo. A empresa "ativa" escolhida no seletor fica no
        // localStorage e só vale se ainda estiver na lista.
        const homeCompany = data.companies as Company;
        const { data: accessRows } = await supabase
          .from('user_company_access')
          .select('companies(*)')
          .eq('user_id', userSupabase.id);
        const extraCompanies = ((accessRows || []) as unknown as { companies: Company | null }[])
          .map(r => r.companies)
          .filter((c): c is Company => !!c && c.id !== homeCompany.id);
        setAccessibleCompanies(extraCompanies.length > 0 ? [homeCompany, ...extraCompanies] : []);

        let currentCompany = homeCompany;
        let activeProfile = data as User;
        try {
          const storedId = Number(localStorage.getItem(ACTIVE_COMPANY_KEY));
          const chosen = extraCompanies.find(c => c.id === storedId);
          if (chosen) {
            currentCompany = chosen;
            activeProfile = { ...activeProfile, company_id: chosen.id };
          }
        } catch { /* localStorage indisponível: fica na empresa de origem */ }

        setProfile(activeProfile);

        if (currentCompany.parent_id) {
          const { data: parentData } = await supabase
            .from('companies')
            .select('settings, name, niche')
            .eq('id', currentCompany.parent_id)
            .maybeSingle();

          if (parentData) {
            currentCompany = {
              ...currentCompany,
              parent_settings: parentData.settings,
              parent_name: parentData.name,
              parent_niche: parentData.niche
            };
          }
        }
        
        setCompany(currentCompany);

        // Verifica se é Parent SuperAdmin
        if (data.role === 'ceo' && currentCompany.is_parent_company) {
            setIsParentSuperAdmin(true);
        } else {
            setIsParentSuperAdmin(false);
        }

        const { data: permsData, error: permsError } = await supabase.rpc('my_permissions');
        if (permsError) {
          console.error('Erro ao carregar permissões:', permsError);
          setPermissions([]);
        } else {
          setPermissions((permsData || []).map((p: { code: string }) => p.code));
        }
      }

      // Verifica se o usuário é um superadmin global
      const { data: superAdminData, error: superAdminError } = await supabase
        .from('superadmins')
        .select('*')
        .eq('user_id', userSupabase.id)
        .maybeSingle();

      if (!superAdminError && superAdminData) {
        setIsGlobalSuperAdmin(true);
      } else {
        setIsGlobalSuperAdmin(false);
      }
      setProfileFetchAttempted(true);
    }
  }, [userSupabase, supabase]);

  useEffect(() => {
    setIsLoading(isSessionLoading || (!!session && !profile && !profileFetchAttempted));

    if (sessionError) {
      setError(sessionError.message);
    }
  }, [isSessionLoading, session, profile, sessionError]);

  useEffect(() => {
    if (session && !profile) {
      fetchProfile();
    } else if (!session) {
      setProfile(null);
      setCompany(null);
      setIsGlobalSuperAdmin(false);
      setIsParentSuperAdmin(false);
    }
  }, [session, profile, fetchProfile]);

  // Verifica periodicamente (e ao voltar o foco na aba) se o acesso do
  // usuário logado foi bloqueado nesse meio tempo — cobre tanto o bloqueio
  // manual quanto o automático por cancelamento de assinatura, já que uma
  // sessão aberta não é derrubada sozinha ao mudar is_active no banco.
  useEffect(() => {
    if (!userSupabase || !profile) return;

    let lastCheck = 0;
    const checkStillActive = async () => {
      // Quem alterna muito entre abas não dispara uma consulta a cada troca
      if (Date.now() - lastCheck < 30 * 1000) return;
      lastCheck = Date.now();

      const { data } = await supabase
        .from('users')
        .select('is_active')
        .eq('id', userSupabase.id)
        .maybeSingle();

      if (data?.is_active === false) {
        toast.error('Seu acesso foi bloqueado. Fale com o administrador da sua empresa.');
        await supabase.auth.signOut();
        localStorage.clear();
        sessionStorage.clear();
        window.location.href = '/login';
      }
    };

    const intervalId = window.setInterval(checkStillActive, 2 * 60 * 1000);
    const onVisibilityChange = () => {
      if (document.visibilityState === 'visible') checkStillActive();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    return () => {
      window.clearInterval(intervalId);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [userSupabase, profile, supabase]);

  const login = useCallback(async (email: string, password: string) => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) {
      throw error;
    }
  }, [supabase]);

  const logout = useCallback(async () => {
    try {
      const { error } = await supabase.auth.signOut();
      if (error) {
        console.error("Erro no logout (global):", error);
        await supabase.auth.signOut({ scope: 'local' });
      }
    } catch (err) {
      console.error("Exceção fatal no logout:", err);
    } finally {
      // Fallback supremo: limpar tudo manualmente caso a API do Supabase trave
      localStorage.clear();
      sessionStorage.clear();
      window.location.href = '/login';
    }
  }, [supabase]);

  // Função para atualizar o perfil no estado global da autenticação
  const updateProfile = useCallback((updatedProfile: User) => {
    setProfile(updatedProfile);
  }, []);

  // Função para atualizar a empresa no estado global da autenticação
  const updateCompany = useCallback((updatedCompany: Company) => {
    setCompany(updatedCompany);
  }, []);


  // Troca a empresa em que o usuário está operando. Recarrega a página para
  // todos os hooks (leads, funis, vendas...) reiniciarem já na empresa nova em
  // vez de misturar dados em cache das duas lojas.
  const switchCompany = useCallback((companyId: number) => {
    try {
      localStorage.setItem(ACTIVE_COMPANY_KEY, String(companyId));
    } catch { /* sem localStorage a troca não persiste */ }
    window.location.reload();
  }, []);

  const isSuperAdmin = isGlobalSuperAdmin || isParentSuperAdmin;
  const isAuthenticated = !!session && !!profile;

  return useMemo(() => ({
    isAuthenticated,
    user: profile,
    company,
    isSuperAdmin,
    isGlobalSuperAdmin,
    isParentSuperAdmin,
    permissions,
    loading: isLoading,
    error,
    login,
    logout,
    updateProfile,
    updateCompany,
    accessibleCompanies,
    switchCompany
  }), [isAuthenticated, profile, company, isSuperAdmin, isGlobalSuperAdmin, isParentSuperAdmin, permissions, isLoading, error, login, logout, updateProfile, updateCompany, accessibleCompanies, switchCompany]);
};

type AuthContextValue = ReturnType<typeof useAuthState>;

const AuthContext = createContext<AuthContextValue | null>(null);

export const AuthProvider = ({ children }: PropsWithChildren) =>
  createElement(AuthContext.Provider, { value: useAuthState() }, children);

export const useAuth = (): AuthContextValue => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth() precisa estar dentro de <AuthProvider>.');
  return ctx;
};